import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '../FirebaseAppContext';
import { useOrganizationId } from '../auth/hooks';
import { FormInput } from '../components/FormInput';
import { useToast } from '../components/ToastProvider';
import './FormPages.css';

type FormData = {
  name: string;
  email: string;
  phone: string;
  driverLicense: string;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[\d\s()-]{7,20}$/;

export default function CustomerForm() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { state, customerOperations } = useAppContext();
  const organizationId = useOrganizationId();
  const isEditMode = customerId !== undefined;
  const customer = isEditMode ? state.customers.find(c => c.id === customerId) : undefined;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  const { control, handleSubmit, setValue, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      name: customer?.name || '',
      email: customer?.email || '',
      phone: customer?.phone || '',
      driverLicense: customer?.driverLicense || '',
    },
    mode: 'onChange'
  });

  useEffect(() => {
    if (customer) {
      setValue('name', customer.name);
      setValue('email', customer.email || '');
      setValue('phone', customer.phone || '');
      setValue('driverLicense', customer.driverLicense);
    }
  }, [customer, setValue]);

  const onSubmit = async (data: FormData) => {
    if (!organizationId) {
      alert('Organization ID is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const customerData = {
        organizationId,
        name: data.name.trim(),
        email: data.email.trim().toLowerCase(),
        phone: data.phone.trim(),
        contactInfo: `${data.email.trim().toLowerCase()} | ${data.phone.trim()}`,
        driverLicense: data.driverLicense.trim(),
      };

      if (isEditMode && customerId) {
        await customerOperations.update(customerId, customerData);
      } else {
        await customerOperations.add(customerData);
      }
      showToast({ message: isEditMode ? 'Customer updated successfully.' : 'Customer added successfully.' });
      navigate('/customers');
    } catch (err) {
      showToast({ message: 'Failed to save customer: ' + (err as Error).message, type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-page">
      <div className="form-page-header">
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/customers')}>← Back</button>
        <h1 className="form-page-title">{isEditMode ? 'Edit Customer' : 'Add New Customer'}</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="form-card">
        <div className="form-stack">
          <Controller
            control={control}
            name="name"
            rules={{ required: 'Name is required', minLength: { value: 2, message: 'Name must be at least 2 characters' } }}
            render={({ field }) => (
              <FormInput
                label="Full Name"
                value={field.value}
                onChange={field.onChange}
                error={errors.name?.message}
                required
                placeholder="Enter customer's full name"
              />
            )}
          />

          <Controller
            control={control}
            name="email"
            rules={{ 
              required: 'Email address is required',
              pattern: {
                value: EMAIL_REGEX,
                message: 'Please enter a valid email address'
              }
            }}
            render={({ field }) => (
              <FormInput
                label="Email Address"
                type="email"
                value={field.value}
                onChange={field.onChange}
                error={errors.email?.message}
                required
                placeholder="name@example.com"
              />
            )}
          />

          <Controller
            control={control}
            name="phone"
            rules={{
              required: 'Phone number is required',
              pattern: {
                value: PHONE_REGEX,
                message: 'Please enter a valid phone number',
              },
              validate: {
                hasEnoughDigits: (value) => {
                  const digits = value.replace(/\D/g, '');
                  return digits.length >= 7 || 'Phone number must include at least 7 digits';
                },
              },
            }}
            render={({ field }) => (
              <FormInput
                label="Phone Number"
                type="tel"
                value={field.value}
                onChange={field.onChange}
                error={errors.phone?.message}
                required
                placeholder="e.g. +1 (555) 123-4567"
              />
            )}
          />

          <Controller
            control={control}
            name="driverLicense"
            rules={{ 
              required: 'Driver license is required',
              minLength: { value: 5, message: 'License must be at least 5 characters' },
              pattern: {
                value: /^[A-Z0-9-]+$/i,
                message: 'License can only contain letters, numbers, and hyphens'
              }
            }}
            render={({ field }) => (
              <FormInput
                label="Driver's License"
                value={field.value}
                onChange={field.onChange}
                error={errors.driverLicense?.message}
                required
                placeholder="License number"
              />
            )}
          />

          <div className="form-actions">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary"
            >
              {isSubmitting ? 'Saving...' : isEditMode ? 'Update Customer' : 'Add Customer'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/customers')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

